import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'

import type { OrderbookRefreshRate } from '@/features/dashboard/lib/orderbook-data'
import type { InstrumentTrade } from '@/types/generated/common/trading'
import type {
  BookEntry,
  IncrementalTicker,
  Ticker,
  VolumeBucket,
} from '@/types/generated/common/types'

// ─── Ref-count registry ───────────────────────────────────────────────────────
// Tracks how many mounted widgets are subscribed to each `${exchange}:${symbol}` key.
// When the last subscriber unmounts, the atoms are cleaned up after a grace period
// so quick revisits (e.g. page tab switch) don't flash empty data.

const _refCounts = new Map<string, number>()
const _cleanupTimers = new Map<string, ReturnType<typeof setTimeout>>()
const CLEANUP_GRACE_MS = 10_000

export function cleanupMarketDataAtoms(key: string): void {
  orderbookAnalyticsAtomFamily.remove(key)
  orderbookBidsAtomFamily.remove(key)
  orderbookAsksAtomFamily.remove(key)
  orderbookTickerAtomFamily.remove(key)
  orderbookIncrementalTickerAtomFamily.remove(key)
  orderbookRefreshRateAtomFamily.remove(key)
  latestNonBlockTradeAtomFamily.remove(key)
  recentTradesAtomFamily.remove(key)
}

export function registerMarketDataKey(key: string): void {
  if (key.includes('__none')) return
  const pending = _cleanupTimers.get(key)
  if (pending) {
    clearTimeout(pending)
    _cleanupTimers.delete(key)
  }
  _refCounts.set(key, (_refCounts.get(key) ?? 0) + 1)
}

export function unregisterMarketDataKey(key: string): void {
  if (key.includes('__none')) return
  const next = (_refCounts.get(key) ?? 1) - 1
  if (next <= 0) {
    _refCounts.delete(key)
    const timer = setTimeout(() => {
      _cleanupTimers.delete(key)
      cleanupMarketDataAtoms(key)
    }, CLEANUP_GRACE_MS)
    _cleanupTimers.set(key, timer)
  } else {
    _refCounts.set(key, next)
  }
}

/**
 * Forces every pending cleanup (queued after a subscriber's last unmount) to
 * run immediately instead of waiting out CLEANUP_GRACE_MS. Only touches keys
 * that already dropped to zero ref count — anything still in use is
 * untouched — so this is safe to call any time, not just during recovery.
 * Used by the runtime recovery system's Level 2 resource cleanup.
 */
export function flushMarketDataCleanup(): number {
  const keys = Array.from(_cleanupTimers.keys())
  keys.forEach((key) => {
    const timer = _cleanupTimers.get(key)
    if (timer) clearTimeout(timer)
    _cleanupTimers.delete(key)
    cleanupMarketDataAtoms(key)
  })
  return keys.length
}

/** Test seam. */
export function resetMarketDataCacheForTest(): void {
  _cleanupTimers.forEach((timer) => clearTimeout(timer))
  _cleanupTimers.clear()
  _refCounts.clear()
}

// Pre-parsed orderbook entry — parseFloat done once on insert
export type NumEntry = BookEntry & { _p: number; _q: number }

export interface OrderBookAnalytics {
  imbalanceRatio: string | null
  updateRateHz: number | null
  volumeWeightedChange: string | null
  totalBidVolume: string | null
  totalAskVolume: string | null
  totalDelta: string | null
  nearMoneyBidVolume: string | null
  nearMoneyAskVolume: string | null
  nearMoneyDelta: string | null
  midPrice: string | null
  volumeDeltaHistory: VolumeBucket[]
}

export const orderbookAnalyticsAtomFamily = atomFamily((_key: string) =>
  atom<OrderBookAnalytics | null>(null)
)

// Orderbook data cached by `${exchange}:${symbol}`
// Persists across widget remounts so page switches don't flash empty data
export const orderbookBidsAtomFamily = atomFamily((_key: string) =>
  atom(new Map<string, NumEntry>())
)

export const orderbookAsksAtomFamily = atomFamily((_key: string) =>
  atom(new Map<string, NumEntry>())
)

export const orderbookTickerAtomFamily = atomFamily((_key: string) => atom<Ticker | null>(null))

// Latest IncrementalTicker cached by `${exchange}:${symbol}`, used by widgets that need
// stats not present on `Ticker` (e.g. turnover, min/max order price).
export const orderbookIncrementalTickerAtomFamily = atomFamily((_key: string) =>
  atom<IncrementalTicker | null>(null)
)

// Recent trades cached by `${exchange}:${symbol}`
export const recentTradesAtomFamily = atomFamily((_key: string) => atom<InstrumentTrade[]>([]))

export interface LatestNonBlockTrade {
  price: number
  timestampNs: string
}

// Shared by every trades consumer for `${exchange}:${symbol}`. Keeping the timestamp with
// the price lets independently-mounted widgets reject stale/out-of-order writes. This also
// lets an Orderbook that mounts behind an existing Recent Trades subscription immediately
// use the current print instead of waiting for the next wire event.
export const latestNonBlockTradeAtomFamily = atomFamily((_key: string) =>
  atom<LatestNonBlockTrade | null>(null)
)

// ─── Option ticker cache (per-symbol, ref-counted) ─────────────────────────
// Latest IncrementalTicker cached by option symbol. Written by use-option-chain-table
// (option chain widget) and read by ticker-tape as initial/fallback data when its own
// subscription hasn't received a snapshot yet for the active option symbol.
// Kept as a per-symbol atomFamily (instead of one global Map) so a busy option chain
// doesn't force a whole-map clone + rerender of every ticker-tape instance on each tick.
export const optionIncrementalTickerAtomFamily = atomFamily((_symbol: string) =>
  atom<IncrementalTicker | null>(null)
)

const _optionTickerRefCounts = new Map<string, number>()
const _optionTickerCleanupTimers = new Map<string, ReturnType<typeof setTimeout>>()

export function registerOptionTickerSymbol(symbol: string): void {
  if (symbol.includes('__none')) return
  const pending = _optionTickerCleanupTimers.get(symbol)
  if (pending) {
    clearTimeout(pending)
    _optionTickerCleanupTimers.delete(symbol)
  }
  _optionTickerRefCounts.set(symbol, (_optionTickerRefCounts.get(symbol) ?? 0) + 1)
}

export function unregisterOptionTickerSymbol(symbol: string): void {
  if (symbol.includes('__none')) return
  const next = (_optionTickerRefCounts.get(symbol) ?? 1) - 1
  if (next <= 0) {
    _optionTickerRefCounts.delete(symbol)
    const timer = setTimeout(() => {
      _optionTickerCleanupTimers.delete(symbol)
      optionIncrementalTickerAtomFamily.remove(symbol)
    }, CLEANUP_GRACE_MS)
    _optionTickerCleanupTimers.set(symbol, timer)
  } else {
    _optionTickerRefCounts.set(symbol, next)
  }
}

// Orderbook delta-flush refresh rate, cached by `${exchange}:${symbol}` — the same key
// scheduleKeyedFlush coalesces onto, so the orderbook widget and a Quick Order popup open
// on the same symbol share one rate instead of one silently overriding the other's choice
// via whichever's scheduleKeyedFlush call happens to arrive first for a given flush cycle.
export const orderbookRefreshRateAtomFamily = atomFamily((_key: string) =>
  atom<OrderbookRefreshRate>('realtime')
)
